import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_SETTINGS = {
  horizon: 168,
  confidence_level: 0.95,
  warmer_life_minutes: 60,
  default_weeks: 2
};

const parseNumber = (value, fallback) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => {
  if (min !== undefined) {
    value = Math.max(min, value);
  }
  if (max !== undefined) {
    value = Math.min(max, value);
  }
  return value;
};

export const sanitizeForecastSettings = (rawValues = {}, fallback = DEFAULT_SETTINGS) => {
  const normalized = {
    horizon: parseNumber(rawValues.horizon, fallback.horizon),
    confidence_level: parseNumber(rawValues.confidence_level, fallback.confidence_level),
    warmer_life_minutes: parseNumber(rawValues.warmer_life_minutes, fallback.warmer_life_minutes),
    default_weeks: parseNumber(rawValues.default_weeks, fallback.default_weeks)
  };

  return {
    horizon: Math.max(1, Math.round(normalized.horizon)),
    confidence_level: clamp(normalized.confidence_level, 0.01, 1),
    warmer_life_minutes: Math.max(0, Math.round(normalized.warmer_life_minutes)),
    default_weeks: Math.max(1, Math.round(normalized.default_weeks))
  };
};

export const useAdminForecastSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const settingsRef = useRef(DEFAULT_SETTINGS);
  const fetchInFlight = useRef(false);

  const loadSettings = useCallback(async (signal) => {
    if (fetchInFlight.current) {
      return settingsRef.current;
    }
    fetchInFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/forecast-settings', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los parámetros de pronóstico.');
      }
      const nextSettings = sanitizeForecastSettings(data.settings || {}, settingsRef.current);
      if (!signal?.aborted) {
        settingsRef.current = nextSettings;
        setSettings(nextSettings);
      }
      return nextSettings;
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar la configuración.');
      }
      throw err;
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
      fetchInFlight.current = false;
    }
  }, []);

  const saveSettings = useCallback(async (values) => {
    const payload = sanitizeForecastSettings(values, settings);
    setSaving(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/forecast-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudo guardar la configuración.');
      }
      settingsRef.current = payload;
      setSettings(payload);
      return payload;
    } catch (err) {
      setError(err?.message || 'Error al guardar la configuración.');
      throw err;
    } finally {
    setSaving(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadSettings(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [loadSettings]);

  return {
    settings,
    loading,
    saving,
    error,
    loadSettings,
    saveSettings
  };
};
